import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { dayjs } from '@app/shared/dayjs-setup';
import { of, tap } from 'rxjs';

import { HttpCacheService } from './http-cache.service';
import { CACHE_ENABLED, CACHE_SETTINGS, CLEAR_HTTP_CACHE } from './http-context';

let persistenceInitialized = false;

/**
 * Request-scoped HTTP caching driven entirely by HttpContext metadata.
 * The interceptor is always in the chain but is a no-op unless caching or
 * explicit cache clearing is requested.
 */
export const cacheInterceptorFn: HttpInterceptorFn = (request, next) => {
  const cacheEnabled = request.context.get(CACHE_ENABLED);
  const clearCache = request.context.get(CLEAR_HTTP_CACHE);

  if (!cacheEnabled && !clearCache) {
    return next(request);
  }

  const httpCacheService = inject(HttpCacheService);
  if (!persistenceInitialized) {
    httpCacheService.setPersistence('local');
    persistenceInitialized = true;
  }

  if (clearCache) {
    httpCacheService.cleanCache();
  }

  if (!cacheEnabled) {
    return next(request);
  }

  const urlExclusionPattern =
    /\/api\/v1\/files\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.\w+\/urls/;

  if (request.method !== 'GET' && !urlExclusionPattern.test(request.url)) {
    return next(request);
  }

  const settings = request.context.get(CACHE_SETTINGS);
  const forceUpdate = settings?.update ?? false;
  const cachedData = forceUpdate ? null : httpCacheService.getCacheData(request.urlWithParams);

  if (cachedData !== null) {
    return of(cachedData.clone());
  }

  const expires =
    typeof settings?.expires === 'number' ? dayjs().add(settings.expires, 'second').toDate() : settings?.expires;

  return next(request).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        httpCacheService.setCacheData(request.urlWithParams, event, settings?.lastUpdated, expires);
      }
    }),
  );
};